import { UploadFile, Visibility } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { applyFileDiff, loadFileInfo } from "../../actions/form";
import getBackendURL from "../../api/api";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useRemoteField } from "../../hooks/useRemoteField";
import { download, preview } from "../../request";
import { ESRFieldProps, ESRState } from "../../types/data";
import { parseFileIds } from "../../utils";

export const ESRMultiFileUploadField = (props: ESRFieldProps) => {
	const { question_id, locked } = props;
	const { esrId: esr_id = "", activeLabel = "", activeId = "" } = useParams();
	const dispatch = useDispatch();

	const [clowderIDsRaw, , , timestamp] = useRemoteField(question_id);
	const fileIDs = parseFileIds(clowderIDsRaw);

	const [value, setValue] = useState("");
	const [filename, setFilename] = useState("");
	const [user, setUser] = useCurrentUser();
	const loading = useSelector((state: ESRState) => state.loading[question_id]);
	const filenames = useSelector((state: ESRState) => state.filenames);

	useEffect(() => {
		setValue("");
	}, [timestamp]);

	useEffect(() => {
		fileIDs.map((fid) => {
			dispatch(loadFileInfo(activeId, fid));
		});
	}, [clowderIDsRaw]);

	const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
		setValue(e.target.value);
		if (!e.target.files || !user) return;
		dispatch(
			applyFileDiff(
				esr_id,
				activeLabel,
				activeId,
				question_id,
				Array.from(e.target.files),
				[],
				locked === -1,
				user.email,
			),
		);
	};

	const handleDelete = (fileId: string) => {
		if (!user) return;
		dispatch(
			applyFileDiff(
				esr_id,
				activeLabel,
				activeId,
				question_id,
				[],
				[fileId],
				locked === -1,
				user.email,
			),
		);
	};

	return (
		<>
			<Stack
				direction="column"
				spacing={1}
				sx={{
					justifyContent: "center",
					alignItems: "stretch",
					flexWrap: "wrap",
					gap: 1,
					width: "100%",
					marginBottom: "3%",
				}}
			>
				<Box sx={{ width: "100%" }}>
					{fileIDs.map((value, index) => {
						if (filenames && filenames.hasOwnProperty(value)) {
							return (
								<Box sx={{ display: "flex", gap: 1, mb: 1 }} key={index}>
									<Chip
										key={index}
										disabled={false} // don't disable the whole chip if preview should work
										label={
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													width: "100%",
												}}
											>
												<Tooltip
													title={getBackendURL(
														`/esr/attachment/?esr_id=${esr_id}&file_id=${value}`,
													)}
												>
													<Typography
														sx={{
															whiteSpace: "normal",
															overflow: "hidden",
															flexGrow: 1,
															padding: "5px",
														}}
													>
														{filenames[value]}
													</Typography>
												</Tooltip>

												<Box sx={{ display: "flex", alignItems: "center" }}>
													<Tooltip title="Preview">
														<IconButton
															size="small"
															color="secondary"
															onClick={(e) => {
																e.stopPropagation();
																preview(
																	getBackendURL(
																		`/esr/attachment/?esr_id=${activeId}&file_id=${value}`,
																	),
																);
															}}
														>
															<Visibility fontSize="small" />
														</IconButton>
													</Tooltip>

													{locked <= 0 && (
														<Tooltip title="Delete">
															<IconButton
																size="small"
																onClick={(e) => {
																	e.stopPropagation();
																	handleDelete(value);
																}}
															>
																<DeleteIcon
																	fontSize="small"
																	sx={{ color: "rgb(211, 47, 47)" }}
																/>
															</IconButton>
														</Tooltip>
													)}
												</Box>
											</Box>
										}
										onClick={() =>
											download(
												getBackendURL(
													`/esr/attachment/?esr_id=${activeId}&file_id=${value}`,
												),
											)
										}
										sx={{
											height: "auto",
											width: "100%",
											justifyContent: "space-between",
											mb: 1,
										}}
									/>
									{/* <Chip
										disabled={locked > 0}
										key={value}
										label={
											<Tooltip
												title={getBackendURL(
													"/esr/attachment/?esr_id=" +
														esr_id +
														"&file_id=" +
														value,
												)}
											>
												<Typography
													sx={{
														whiteSpace: "normal",
														overflow: "hidden",
														flexGrow: 1,
														padding: "5px",
													}}
												>
													{filenames[value]}
												</Typography>
											</Tooltip>
										}
										onClick={() =>
											download(
												getBackendURL(
													`/esr/attachment/?esr_id=${activeId}&file_id=${value}`,
												),
											)
										}
										onDelete={() => handleDelete(value)}
										deleteIcon={
											<Tooltip title="Delete">
												<span>
													<IconButton size="small" disabled={locked > 0}>
														<DeleteIcon
															fontSize="small"
															sx={{ color: "rgb(211, 47, 47)" }}
														/>
													</IconButton>
												</span>
											</Tooltip>
										}
										sx={{
											height: "auto",
											width: "100%",
											justifyContent: "space-between",
											mb: 1,
										}}
									/>
									<IconButton
										aria-label="preview file"
										color="secondary"
										onClick={() =>
											preview(
												getBackendURL(
													`/esr/attachment/?esr_id=${activeId}&file_id=${value}`,
												),
											)
										}
									>
										<Visibility />
									</IconButton> */}
								</Box>
							);
						}
					})}
				</Box>
			</Stack>
			{filename != "" ? (
				<>
					<Typography fontWeight={500}>{filename}</Typography>
				</>
			) : (
				<></>
			)}
			<Stack direction="row" sx={{ gap: "4px" }}>
				<Button
					disabled={locked > 0}
					component="label"
					variant="contained"
					startIcon={
						loading ? (
							<CircularProgress size={20} sx={{ color: "white" }} />
						) : (
							<UploadFile />
						)
					}
				>
					{fileIDs.length > 0 ? <>Add Files</> : <>Upload</>}
					<input
						id={question_id}
						type="file"
						multiple
						style={{
							clipPath: "inset(50%)",
							height: 1,
							width: 1,
						}}
						value={value}
						onChange={handleUpload}
					/>
				</Button>
			</Stack>
		</>
	);
};
